// Odvození menu z GET /ja (docs/08-autorizace.md §5.4). Server posílá povolené `sekce[]` a `kategorie[]`
// v oficiálním pořadí — tady se jen skládá struktura pro lištu a doplňují popisky závislé na roli.
// Co uživatel smí, rozhoduje výhradně server; nic se tu neodvozuje z role navíc.
import type { Ja } from '@ecoser/shared';
import { maRoli } from '../komponenty/prava';
import { nazevSekce } from '../prihlaseni/nazvy';
import { cestaSekce } from './sekce-cesty';

export interface PolozkaMenu {
  klic: string;
  nazev: string;
  cesta: string;
}

export interface SkupinaMenu {
  klic: string;
  nazev: string;
  polozky: PolozkaMenu[];
}

export interface Menu {
  /** Domů — vždy první, mimo skupiny. */
  domu: PolozkaMenu;
  /** Kategorie z /ja v pořadí serveru (prázdné server neposílá). */
  skupiny: SkupinaMenu[];
  /** Povolené sekce bez kategorie (např. asistent, pravidla_ai) — za skupinami. */
  ostatni: PolozkaMenu[];
}

/**
 * Správcovský režim ISŘ se všemi záložkami: admin nebo příznak `spravce_isr` (08 §2 řádek `iso`; backend
 * `iso.spravovat` = A + množina `spravce_isr`).
 */
export function jeSpravceIsr(ja: Ja): boolean {
  return maRoli(ja, 'admin') || ja.zamestnanec?.spravce_isr === true;
}

/**
 * Pohled vedení na ISŘ: správce ISŘ, nebo jednatel i bez příznaku — ten vidí vždy Přehled, KPI a Přezkoumání ISŘ
 * (rozhodnutí zadavatele 24. 9. 2026, 05/709; backend `iso-prezkoumani/prava.ts` — schvalovatel a vedení) a vedle
 * nich svá školení a hlášení. Ostatní záložky ISŘ jen se správcovstvím (příznak „Správce ISŘ“ na kartě zaměstnance).
 */
export function vidiVedeniIsr(ja: Ja): boolean {
  return jeSpravceIsr(ja) || maRoli(ja, 'jednatel');
}

/** Název sekce v menu podle role (§5.4 bod 2): mlynář „Drcení a štítky“, ISŘ podle správcovství. */
export function nazevSekcePodleRole(klic: string, ja: Ja): string {
  if (klic === 'sklad_evidence' && ja.role_efektivni === 'mlynar') {
    return 'Drcení a štítky';
  }
  if (klic === 'iso') {
    return vidiVedeniIsr(ja) ? 'Systém řízení (ISŘ)' : 'Moje školení a hlášení';
  }
  return nazevSekce(klic);
}

function polozka(klic: string, ja: Ja): PolozkaMenu {
  return { klic, nazev: nazevSekcePodleRole(klic, ja), cesta: cestaSekce(klic) };
}

/**
 * Seřadí klíče položek jedné kategorie podle osobního pořadí uživatele (`menu_osobni_poradi`, Nastavení › Menu).
 * Osobní pořadí řadí jen uvnitř kategorie; položky, které v něm nejsou (přibyly po uložení), zůstanou za nimi
 * v oficiálním pořadí serveru.
 */
export function seraditPodleOsobnihoPoradi(
  klice: readonly string[],
  osobni: readonly string[] | null | undefined,
): string[] {
  if (!osobni || osobni.length === 0) return [...klice];
  const index = (klic: string) => {
    const i = osobni.indexOf(klic);
    return i === -1 ? Number.POSITIVE_INFINITY : i;
  };
  return klice
    .map((klic, poradi) => ({ klic, poradi }))
    .sort((a, b) => {
      const x = index(a.klic);
      const y = index(b.klic);
      if (x === y) return a.poradi - b.poradi;
      return x - y;
    })
    .map((p) => p.klic);
}

/**
 * Sestaví menu z /ja: Domů, kategorie serveru, zbylé povolené sekce bez kategorie. S `osobniPoradi` (uživatel si
 * v Nastavení › Menu přeskládal položky) se položky řadí uvnitř kategorií po svém; kategorie určuje vždy server.
 */
export function sestavitMenu(ja: Ja, osobniPoradi?: readonly string[] | null): Menu {
  const povolene = new Set(ja.sekce ?? []);
  const vKategoriich = new Set<string>();

  const skupiny: SkupinaMenu[] = [];
  for (const kategorie of ja.kategorie ?? []) {
    const klice = (kategorie.sekce ?? []).filter((s) => povolene.size === 0 || povolene.has(s));
    if (klice.length === 0) continue;
    for (const k of klice) vKategoriich.add(k);
    const nazev = kategorie.nazev ?? kategorie.klic ?? '';
    skupiny.push({
      klic: kategorie.klic ?? nazev,
      nazev,
      polozky: seraditPodleOsobnihoPoradi(klice, osobniPoradi).map((k) => polozka(k, ja)),
    });
  }

  const ostatni = [...povolene]
    .filter((k) => k !== 'home' && !vKategoriich.has(k))
    .map((k) => polozka(k, ja));

  return { domu: polozka('home', ja), skupiny, ostatni };
}

/** Všechny položky menu v pořadí lišty (Domů, skupiny, ostatní). */
export function vsechnyPolozky(menu: Menu): PolozkaMenu[] {
  return [menu.domu, ...menu.skupiny.flatMap((s) => s.polozky), ...menu.ostatni];
}

/**
 * Přistávací sekce po přihlášení (§5.4 bod 5): řidič, skladník a auditor → Můj den, mlynář →
 * Náš sklad (Drcení a štítky), ostatní Domů. Pokud server sekci nepovolil, zůstává Domů.
 */
export function pristavaciSekce(ja: Ja): string {
  const role = ja.role_efektivni || ja.role || '';
  let cil = 'home';
  if (role === 'ridic' || role === 'skladnik' || role === 'auditor') {
    cil = 'muj_den';
  } else if (role === 'mlynar') {
    cil = 'sklad_evidence';
  }
  return cil !== 'home' && (ja.sekce ?? []).includes(cil) ? cil : 'home';
}
